// src/modules/app-views/products/pages/Document/tabs/AiInfoTab/RealestateDataSection.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Flex, Descriptions, Collapse, Tag, theme } from 'antd';
import {
  HomeOutlined,
  EnvironmentOutlined,
  ToolOutlined,
  BankOutlined,
} from '@ant-design/icons';
import translateX from 'utils/translateX';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const RealestateDataSection = ({ data, isMobile }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();
  const re = data?.realestateData;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const hasData = re && (re.propertyType || re.totalArea || re.city || re.registryNumber);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. HELPERS
  // ─────────────────────────────────────────────────────────────────────────────
  const formatCurrency = value => {
    if (value === undefined || value === null) return '-';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const areaUnitLabel = re?.areaUnit === 'hectares' ? 'ha' : 'm\u00B2';

  const hasInfra = re?.infrastructure && Object.values(re.infrastructure).some(v => v !== undefined && v !== null);
  const hasBuilding = re?.building && Object.values(re.building).some(v => v !== undefined && v !== null);
  const hasAgricultural = re?.agricultural && Object.values(re.agricultural).some(v => v !== undefined && v !== null);
  const hasCommercial = re?.commercial && Object.values(re.commercial).some(v => v !== undefined && v !== null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (!hasData) return null;

  return (
    <>
      {/* PROPERTY INFO */}
      <Collapse
        className="collapsed-content-fit"
        collapsible="icon"
        defaultActiveKey={['realestate-info']}
        style={{ marginBottom: 16 }}
        items={[
          {
            key: 'realestate-info',
            label: (
              <Flex align="center" gap={8}>
                <HomeOutlined style={{ color: token.colorPrimary }} />
                <span style={{ fontWeight: 600 }}>{translateX('realestate_info')}</span>
              </Flex>
            ),
            showArrow: false,
            children: (
              <Descriptions
                bordered
                size="small"
                column={1}
                layout={isMobile ? 'vertical' : 'horizontal'}
                styles={{ label: { fontWeight: 500 } }}
              >
                {re.propertyType && (
                  <Descriptions.Item label={translateX('property_type')}>
                    <Tag color="blue">{re.propertyType}</Tag>
                  </Descriptions.Item>
                )}
                {re.propertyStatus && (
                  <Descriptions.Item label={translateX('property_status')}>
                    <Tag color={re.propertyStatus === 'available' ? 'green' : re.propertyStatus === 'sold' ? 'red' : 'orange'}>
                      {re.propertyStatus}
                    </Tag>
                  </Descriptions.Item>
                )}
                {re.totalArea !== undefined && re.totalArea !== null && (
                  <Descriptions.Item label={translateX('total_area')}>
                    {re.totalArea.toLocaleString('pt-BR')} {areaUnitLabel}
                  </Descriptions.Item>
                )}
                {re.pricePerAreaUnit !== undefined && re.pricePerAreaUnit !== null && (
                  <Descriptions.Item label={translateX('price_per_area')}>
                    {formatCurrency(re.pricePerAreaUnit)}/{areaUnitLabel}
                  </Descriptions.Item>
                )}
                {re.registryNumber && (
                  <Descriptions.Item label={translateX('registry_number')}>
                    {re.registryNumber}
                  </Descriptions.Item>
                )}
                {re.iptuCode && (
                  <Descriptions.Item label={translateX('iptu_code')}>
                    {re.iptuCode}
                  </Descriptions.Item>
                )}
                {re.zone && (
                  <Descriptions.Item label={translateX('zone')}>
                    <Tag>{re.zone}</Tag>
                  </Descriptions.Item>
                )}
                {re.topography && (
                  <Descriptions.Item label={translateX('topography')}>
                    {re.topography}
                  </Descriptions.Item>
                )}
                {re.hasWaterFront !== undefined && (
                  <Descriptions.Item label={translateX('has_waterfront')}>
                    {re.hasWaterFront ? (
                      <Tag color="blue">{translateX('yes')}</Tag>
                    ) : (
                      <Tag>{translateX('no')}</Tag>
                    )}
                  </Descriptions.Item>
                )}
                {re.financingAvailable !== undefined && (
                  <Descriptions.Item label={translateX('financing_available')}>
                    {re.financingAvailable ? (
                      <Tag color="green">{translateX('yes')}</Tag>
                    ) : (
                      <Tag color="red">{translateX('no')}</Tag>
                    )}
                  </Descriptions.Item>
                )}
                {re.financingTerms && (
                  <Descriptions.Item label={translateX('financing_terms')}>
                    {re.financingTerms}
                  </Descriptions.Item>
                )}
              </Descriptions>
            ),
          },
        ]}
      />

      {/* LOCATION */}
      {(re.cep || re.neighborhood || re.city || re.state) && (
        <Collapse
          className="collapsed-content-fit"
          collapsible="icon"
          defaultActiveKey={['location']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'location',
              label: (
                <Flex align="center" gap={8}>
                  <EnvironmentOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('location')}</span>
                </Flex>
              ),
              showArrow: false,
              children: (
                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  layout={isMobile ? 'vertical' : 'horizontal'}
                  styles={{ label: { fontWeight: 500 } }}
                >
                  {re.cep && (
                    <Descriptions.Item label="CEP">
                      {re.cep}
                    </Descriptions.Item>
                  )}
                  {re.neighborhood && (
                    <Descriptions.Item label={translateX('neighborhood')}>
                      {re.neighborhood}
                    </Descriptions.Item>
                  )}
                  {re.city && (
                    <Descriptions.Item label={translateX('city')}>
                      {re.city}
                    </Descriptions.Item>
                  )}
                  {re.state && (
                    <Descriptions.Item label={translateX('state')}>
                      {re.state}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              ),
            },
          ]}
        />
      )}

      {/* INFRASTRUCTURE */}
      {hasInfra && (
        <Collapse
          className="collapsed-content-fit"
          collapsible="icon"
          defaultActiveKey={['infrastructure']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'infrastructure',
              label: (
                <Flex align="center" gap={8}>
                  <ToolOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('infrastructure')}</span>
                </Flex>
              ),
              showArrow: false,
              children: (
                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  layout={isMobile ? 'vertical' : 'horizontal'}
                  styles={{ label: { fontWeight: 500 } }}
                >
                  {renderBoolField(re.infrastructure.hasElectricity, translateX('has_electricity'))}
                  {renderBoolField(re.infrastructure.hasWater, translateX('has_water'))}
                  {renderBoolField(re.infrastructure.hasSewage, translateX('has_sewage'))}
                  {renderBoolField(re.infrastructure.hasGasLine, translateX('has_gas_line'))}
                  {renderBoolField(re.infrastructure.hasInternet, translateX('has_internet'))}
                  {renderBoolField(re.infrastructure.hasAsphaltAccess, translateX('has_asphalt_access'))}
                  {re.infrastructure.waterSource && (
                    <Descriptions.Item label={translateX('water_source')}>
                      {re.infrastructure.waterSource}
                    </Descriptions.Item>
                  )}
                  {re.infrastructure.sewageType && (
                    <Descriptions.Item label={translateX('sewage_type')}>
                      {re.infrastructure.sewageType}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              ),
            },
          ]}
        />
      )}

      {/* BUILDING */}
      {hasBuilding && (
        <Collapse
          className="collapsed-content-fit"
          collapsible="icon"
          defaultActiveKey={['building']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'building',
              label: (
                <Flex align="center" gap={8}>
                  <BankOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('building_info')}</span>
                </Flex>
              ),
              showArrow: false,
              children: (
                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  layout={isMobile ? 'vertical' : 'horizontal'}
                  styles={{ label: { fontWeight: 500 } }}
                >
                  {re.building.buildingName && (
                    <Descriptions.Item label={translateX('building_name')}>
                      {re.building.buildingName}
                    </Descriptions.Item>
                  )}
                  {re.building.floor !== undefined && re.building.floor !== null && (
                    <Descriptions.Item label={translateX('floor')}>
                      {re.building.floor}
                    </Descriptions.Item>
                  )}
                  {re.building.bedrooms !== undefined && re.building.bedrooms !== null && (
                    <Descriptions.Item label={translateX('bedrooms')}>
                      {re.building.bedrooms}
                    </Descriptions.Item>
                  )}
                  {re.building.suites !== undefined && re.building.suites !== null && (
                    <Descriptions.Item label={translateX('suites')}>
                      {re.building.suites}
                    </Descriptions.Item>
                  )}
                  {re.building.bathrooms !== undefined && re.building.bathrooms !== null && (
                    <Descriptions.Item label={translateX('bathrooms')}>
                      {re.building.bathrooms}
                    </Descriptions.Item>
                  )}
                  {re.building.garages !== undefined && re.building.garages !== null && (
                    <Descriptions.Item label={translateX('garages')}>
                      {re.building.garages}
                    </Descriptions.Item>
                  )}
                  {re.building.privateArea !== undefined && re.building.privateArea !== null && (
                    <Descriptions.Item label={translateX('private_area')}>
                      {re.building.privateArea} m&sup2;
                    </Descriptions.Item>
                  )}
                  {re.building.condominiumFee !== undefined && re.building.condominiumFee !== null && (
                    <Descriptions.Item label={translateX('condominium_fee')}>
                      {formatCurrency(re.building.condominiumFee)}
                    </Descriptions.Item>
                  )}
                  {re.building.constructionYear !== undefined && re.building.constructionYear !== null && (
                    <Descriptions.Item label={translateX('construction_year')}>
                      {re.building.constructionYear}
                    </Descriptions.Item>
                  )}
                  {renderBoolField(re.building.hasElevator, translateX('has_elevator'))}
                  {re.building.amenities?.length > 0 && (
                    <Descriptions.Item label={translateX('amenities')}>
                      <Flex wrap="wrap" gap={4}>
                        {re.building.amenities.map((a, i) => (
                          <Tag key={i} color="blue" style={{ margin: 0 }}>{a}</Tag>
                        ))}
                      </Flex>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              ),
            },
          ]}
        />
      )}

      {/* AGRICULTURAL */}
      {hasAgricultural && (
        <Collapse
          className="collapsed-content-fit"
          collapsible="icon"
          defaultActiveKey={['agricultural']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'agricultural',
              label: (
                <Flex align="center" gap={8}>
                  <EnvironmentOutlined style={{ color: token.colorSuccess }} />
                  <span style={{ fontWeight: 600 }}>{translateX('agricultural_info')}</span>
                </Flex>
              ),
              showArrow: false,
              children: (
                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  layout={isMobile ? 'vertical' : 'horizontal'}
                  styles={{ label: { fontWeight: 500 } }}
                >
                  {re.agricultural.cropTypes?.length > 0 && (
                    <Descriptions.Item label={translateX('crop_types')}>
                      <Flex wrap="wrap" gap={4}>
                        {re.agricultural.cropTypes.map((c, i) => (
                          <Tag key={i} color="green" style={{ margin: 0 }}>{c}</Tag>
                        ))}
                      </Flex>
                    </Descriptions.Item>
                  )}
                  {renderBoolField(re.agricultural.hasIrrigation, translateX('has_irrigation'))}
                  {renderBoolField(re.agricultural.hasFencing, translateX('has_fencing'))}
                  {renderBoolField(re.agricultural.hasBarns, translateX('has_barns'))}
                  {renderBoolField(re.agricultural.hasMainHouse, translateX('has_main_house'))}
                  {re.agricultural.animalCapacity !== undefined && re.agricultural.animalCapacity !== null && (
                    <Descriptions.Item label={translateX('animal_capacity')}>
                      {re.agricultural.animalCapacity}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              ),
            },
          ]}
        />
      )}

      {/* COMMERCIAL */}
      {hasCommercial && (
        <Collapse
          className="collapsed-content-fit"
          collapsible="icon"
          defaultActiveKey={['commercial']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'commercial',
              label: (
                <Flex align="center" gap={8}>
                  <BankOutlined style={{ color: token.colorWarning }} />
                  <span style={{ fontWeight: 600 }}>{translateX('commercial_info')}</span>
                </Flex>
              ),
              showArrow: false,
              children: (
                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  layout={isMobile ? 'vertical' : 'horizontal'}
                  styles={{ label: { fontWeight: 500 } }}
                >
                  {re.commercial.commercialType && (
                    <Descriptions.Item label={translateX('commercial_type')}>
                      {re.commercial.commercialType}
                    </Descriptions.Item>
                  )}
                  {re.commercial.frontageWidth !== undefined && re.commercial.frontageWidth !== null && (
                    <Descriptions.Item label={translateX('frontage_width')}>
                      {re.commercial.frontageWidth} m
                    </Descriptions.Item>
                  )}
                  {re.commercial.ceilingHeight !== undefined && re.commercial.ceilingHeight !== null && (
                    <Descriptions.Item label={translateX('ceiling_height')}>
                      {re.commercial.ceilingHeight} m
                    </Descriptions.Item>
                  )}
                  {renderBoolField(re.commercial.hasLoadingArea, translateX('has_loading_area'))}
                  {re.commercial.permittedUsage && (
                    <Descriptions.Item label={translateX('permitted_usage')}>
                      {re.commercial.permittedUsage}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              ),
            },
          ]}
        />
      )}
    </>
  );
};

// *******************************************************************************
// HELPERS
// *******************************************************************************
function renderBoolField(value, label) {
  if (value === undefined || value === null) return null;
  return (
    <Descriptions.Item label={label}>
      {value ? (
        <Tag color="green">{translateX('yes')}</Tag>
      ) : (
        <Tag color="red">{translateX('no')}</Tag>
      )}
    </Descriptions.Item>
  );
}

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default RealestateDataSection;
